import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { billingApi, type BillingCycle } from '../api/client'
import { useAuthStore } from '../store/authStore'
import { formatMoney, catalogPrice } from '../lib/pricing'
import { useProduct } from '../hooks/useCatalog'
import { useDisplayCurrency } from '../store/currencyStore'
import { useCartStore } from '../store/cartStore'
import CurrencyPicker from '../components/CurrencyPicker'
import PaymentLogos from '../components/PaymentLogos'

export default function PricingPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const appName = 'Provisyer'
  const supportEmail: string | undefined = undefined
  const [checkoutError, setCheckoutError] = useState('')
  const [qty, setQty] = useState(1)
  const [cycle, setCycle] = useState<BillingCycle>('monthly')
  const addToCart = useCartStore((s) => s.add)
  const setCartCycle = useCartStore((s) => s.setCycle)

  // The free tier description still comes from /billing/plans. Every price on
  // this page comes from the catalog, which is the table the checkout prices
  // against, so the sticker here and the charge there cannot disagree.
  const { data: plansData } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: billingApi.getPlans,
  })

  const freePlan = plansData?.plans.find((p) => p.id === 'free') ?? null
  const slot = useProduct('slot')
  const currency = useDisplayCurrency()

  const perInstance = catalogPrice(slot, cycle, currency)
  const total = perInstance * qty
  // Yearly savings vs paying monthly for 12 months, in the display currency.
  const yearlySavings = slot
    ? catalogPrice(slot, 'monthly', currency) * 12 - catalogPrice(slot, 'yearly', currency)
    : 0

  const handleStartFree = () => {
    if (!isAuthenticated) navigate('/register')
    else navigate('/dashboard')
  }

  // Buying always goes through the cart. That is where the order is summarised
  // and where the buyer approves the distance-sales agreement — no purchase
  // path may skip that step.
  const handleBuy = () => {
    setCheckoutError('')
    addToCart('slot', qty, 50)
    setCartCycle(cycle)
    navigate('/sepet')
  }

  const paidFeatures = [
    t('pricing.paidFeatureCustomDomain'),
    t('pricing.paidFeatureCustomAddons'),
    t('pricing.paidFeatureBackups'),
    t('pricing.paidFeatureScripts'),
    t('pricing.paidFeatureClone'),
    t('pricing.paidFeatureLargeStorage'),
  ]

  return (
    <div className="relative min-h-screen bg-[#fbfbfa]">
      {/* Atmospheric glow — echoes the landing "shine" without shouting */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(90%_120%_at_85%_-10%,rgba(2,132,199,0.08),transparent_55%),radial-gradient(60%_90%_at_5%_0%,rgba(255,79,26,0.05),transparent_60%)]" />
      <nav className="relative bg-white/80 backdrop-blur-md border-b border-black/[0.06] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-lg font-bold text-gray-900">{appName}</Link>
        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <Link to="/dashboard" className="text-sm text-brand-600 hover:text-brand-700 font-medium">
              {t('pricing.dashboard')}
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
                {t('pricing.signIn')}
              </Link>
              <Link
                to="/register"
                className="bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                {t('pricing.getStarted')}
              </Link>
            </>
          )}
        </div>
      </nav>

      <div className="relative max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[1.5px] text-brand-600 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
            {t('pricing.eyebrow')}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-4">{t('pricing.title')}</h1>
          <p className="text-lg text-gray-500">{t('pricing.subtitle')}</p>
        </div>

        {/* Currency + cycle toggles */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-10">
          <CurrencyPicker />
          <div className="inline-flex items-center bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setCycle('monthly')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                cycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('pricing.cycleMonthly')}
            </button>
            <button
              onClick={() => setCycle('yearly')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                cycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('pricing.cycleYearly')}
              {yearlySavings > 0 && (
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">
                  {t('pricing.cycleYearlySave', { amount: formatMoney(yearlySavings, currency, i18n.language) })}
                </span>
              )}
            </button>
          </div>
        </div>

        {checkoutError && (
          <div className="max-w-md mx-auto mb-8 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {checkoutError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Free card */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 flex flex-col">
            <h2 className="text-xl font-bold text-gray-900 mb-1">{freePlan?.name ?? t('pricing.freeName')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('pricing.instanceCount', { count: freePlan?.instance_limit ?? 1 })}
            </p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-gray-900">{t('pricing.free')}</span>
              <span className="text-gray-400 text-sm ml-1">/{t('pricing.mo')}</span>
            </div>
            <ul className="space-y-2 mb-8 flex-1">
              {(freePlan?.features ?? []).map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              onClick={handleStartFree}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              {t('pricing.startFree')}
            </button>
          </div>

          {/* Paid (per-instance) card */}
          <div className="bg-white rounded-2xl border-2 border-brand-500 shadow-lg shadow-brand-100 p-6 flex flex-col">
            <div className="text-xs font-semibold text-brand-600 bg-brand-50 rounded-full px-3 py-1 self-start mb-4">
              {t('pricing.popular')}
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">{t('pricing.paidName')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('pricing.paidTagline')}
            </p>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-gray-900">{formatMoney(perInstance, currency, i18n.language)}</span>
                <span className="text-gray-400 text-sm">
                  /{cycle === 'yearly' ? t('pricing.instancePerYr') : t('pricing.instancePerMo')}
                </span>
              </div>
            </div>
            <ul className="space-y-2 mb-8 flex-1">
              {paidFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-gray-600 font-medium">{t('pricing.slotQty')}</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100 font-bold"
                  >
                    -
                  </button>
                  <span className="w-8 text-center font-semibold text-gray-900">{qty}</span>
                  <button
                    onClick={() => setQty((q) => Math.min(50, q + 1))}
                    className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100 font-bold"
                  >
                    +
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {t('pricing.slotTotal', { total: formatMoney(total, currency, i18n.language) })}
                <span className="text-gray-400 ml-1">
                  /{cycle === 'yearly' ? t('pricing.yr') : t('pricing.mo')}
                </span>
              </p>
            </div>

            <button
              onClick={handleBuy}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
            >
              {t('products.addToCart')}
            </button>
            <p className="mt-2 text-xs text-gray-400 text-center">
              {t('products.vatNote', { rate: 20 })}
            </p>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <PaymentLogos withCaption />
            </div>
          </div>
        </div>

        {/* Enterprise card */}
        <div className="mt-8 bg-gray-900 rounded-2xl p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="flex-1">
              <div className="text-xs font-semibold text-amber-400 uppercase tracking-widest mb-3">
                {t('pricing.enterpriseBadge')}
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{t('pricing.enterpriseName')}</h2>
              <p className="text-gray-400 text-sm mb-6">{t('pricing.enterpriseTagline')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {([
                  'enterpriseFeature247Support',
                  'enterpriseFeatureSysAdmin',
                  'enterpriseFeatureUnlimitedResources',
                  'enterpriseFeatureCustomModules',
                  'enterpriseFeatureSLA',
                  'enterpriseFeatureDedicatedManager',
                  'enterpriseFeatureOnPremise',
                  'enterpriseFeatureTraining',
                ] as const).map((key) => (
                  <div key={key} className="flex items-center gap-2 text-sm text-gray-300">
                    <svg className="w-4 h-4 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t(`pricing.${key}`)}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 text-center md:min-w-[180px]">
              <div className="text-3xl font-bold text-white">{t('pricing.contactUs')}</div>
              <p className="text-sm text-gray-400">{t('pricing.enterprisePriceNote')}</p>
              <a
                href={supportEmail ? `mailto:${supportEmail}` : 'mailto:info@provisyer.com'}
                className="mt-2 w-full px-6 py-3 rounded-xl text-sm font-semibold bg-amber-500 text-white hover:bg-amber-400 transition-colors text-center"
              >
                {t('pricing.contactSales')}
              </a>
            </div>
          </div>
        </div>

        {/* Reseller CTA */}
        <div className="mt-8 bg-white rounded-2xl border-2 border-gray-200 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-xs font-semibold text-brand-600 uppercase tracking-widest mb-2">
              {t('pricing.resellerBadge')}
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">{t('pricing.resellerTitle')}</h2>
            <p className="text-sm text-gray-500">{t('pricing.resellerText')}</p>
          </div>
          <a
            href={`mailto:${supportEmail ?? 'info@provisyer.com'}?subject=${encodeURIComponent(t('pricing.resellerMailSubject'))}`}
            className="shrink-0 px-6 py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors text-center"
          >
            {t('pricing.resellerCta')}
          </a>
        </div>

        <p className="text-center text-sm text-gray-400 mt-12">
          {t('pricing.footer')}
        </p>
      </div>
    </div>
  )
}
